import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Stack } from "@fluentui/react";
import { Box } from "@mui/material";
import { TeamsFxContext } from "../../components/Context";
import { useTheme } from "../../theme/OAS365Theme";
import { useTools } from "../../redux/hooks/useTools";
import {
  CHECKIN_NEW_PAGE_NAME,
  CHECKIN_NEW,
  DATA_LOAD_STATUS,
  THEME_MODE,
  CHECKIN_FORM_LAYOUT_MIN_WIDTH_PX,
  DOC_CHECK_IN_TYPE,
} from "../../redux/constants/metaData";
import { APP_ROUTES } from "../../routes/routeConfig";
import CheckInNew from "../check-in-new/components/CheckInNew";
import CheckinNewRevision from "../check-in-new-revision/components/CheckinNewRevision";

const CHECKIN_TAB = {
  newDocument: "newDocument",
  newRevision: "newRevision",
};

const SEGMENT_GAP_PX = 8;
const SEGMENT_MIN_HEIGHT_PX = 38;
const SEGMENT_RADIUS_PX = 5;

function CheckInPage() {
  const theme = useTheme();
  const { themeString } = useContext(TeamsFxContext);
  const location = useLocation();
  const navigate = useNavigate();
  const tools = useTools();
  const selectedDocument = location.state?.selectedDocument;
  const cameFromAllDocuments = location.state?.from === APP_ROUTES.allDocuments;
  const disableNewRevisionTab =
    selectedDocument?.documentMode === DOC_CHECK_IN_TYPE.checkinNew;

  const initialTab = useMemo(() => {
    if (disableNewRevisionTab) {
      return CHECKIN_TAB.newDocument;
    }
    return location.state?.initialTab === CHECKIN_TAB.newRevision
      ? CHECKIN_TAB.newRevision
      : CHECKIN_TAB.newDocument;
  }, [disableNewRevisionTab, location.state?.initialTab]);
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (disableNewRevisionTab && activeTab === CHECKIN_TAB.newRevision) {
      setActiveTab(CHECKIN_TAB.newDocument);
    }
  }, [activeTab, disableNewRevisionTab]);

  useEffect(() => {
    if (!selectedDocument) {
      if (!cameFromAllDocuments) {
        navigate(APP_ROUTES.checkInNew, { replace: true });
      }
      return;
    }

    const title =
      selectedDocument.name?.substring(
        0,
        selectedDocument.name?.indexOf(".") ?? selectedDocument.name?.length,
      ) ?? "";
    const fileName = selectedDocument.name ?? "";
    const fileType = selectedDocument.fileType ?? "";
    const base64str =
      typeof selectedDocument.base64str === "string"
        ? selectedDocument.base64str
        : "";

    tools.onDocTitleChange(title);
    const allFiles = [
      {
        name: fileName,
        type: fileType,
        size: selectedDocument.size != null ? String(selectedDocument.size) : "0 KB",
        base64: "",
        base64str,
        // Keep raw File for Share-style chunked browse upload.
        file: selectedDocument.file ?? null,
        source: selectedDocument.source,
      },
    ];
    tools.onFileUploadChange(allFiles, allFiles.length);
    tools.onCheckinPageNameChanged(CHECKIN_NEW_PAGE_NAME.basic);
    tools.onDocCheckinTypeChange(CHECKIN_NEW);
    tools.onSetDocumentToDeleteOneDrive({
      checkinSuccess: false,
      id: selectedDocument.key,
      deleteRequired: true,
      checkinType: CHECKIN_NEW,
    });
    tools.onBasicDataProgressChange(DATA_LOAD_STATUS.onLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- same init behavior as previous Check-in New page
  }, [selectedDocument]);

  const activeGreen =
    theme.colors?.themeDarkColor ?? theme.colors?.themeColor ?? "#249f5d";
  const inactiveBorder =
    theme.colors?.Addovation20Grey ?? theme.colors?.borderColor ?? "#d8d8d8";
  const inactiveText = theme.colors?.fontColor ?? "#111827";
  /** Inactive tabs use white pill; dark theme global fontColor is light — use black for contrast. */
  const inactiveTabLabelColor =
    themeString === THEME_MODE.dark ? "#000000" : inactiveText;
  const segmentBase = {
    flex: 1,
    minHeight: SEGMENT_MIN_HEIGHT_PX,
    maxHeight: SEGMENT_MIN_HEIGHT_PX,
    borderRadius: `${SEGMENT_RADIUS_PX}px`,
    fontFamily: "Poppins, sans-serif",
    fontSize: 12,
    cursor: "pointer",
    borderStyle: "solid",
    borderWidth: 1,
    outline: "none",
    transition:
      "background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease",
  };

  return (
    <Stack
      enableScopedSelectors
      styles={{
        root: {
          background: theme.colors.backgroundColor,
          overflow: "hidden",
          height: "100%",
        },
      }}
      tokens={{ childrenGap: 0, padding: 0 }}
    >
      <Stack.Item
        grow
        styles={{
          root: {
            flex: "1 1 auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
          },
        }}
      >
        <div
          style={{
            minWidth: CHECKIN_FORM_LAYOUT_MIN_WIDTH_PX,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <Stack
            enableScopedSelectors
            styles={{
              root: {
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                background: theme.colors.backgroundColor,
              },
            }}
            tokens={{ childrenGap: 0, padding: 0 }}
          >
            <Stack.Item
              style={{
                background: theme.colors.backgroundColor,
                padding: "15px 300px 0 300px",
              }}
            >
              <Box
                role="tablist"
                aria-label="Check-in type"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "stretch",
                    width: "100%",
                    maxWidth: "100%",
                    gap: `${SEGMENT_GAP_PX}px`,
                  }}
                >
                  <button
                    type="button"
                    role="tab"
                    id="checkin-tab-new-document"
                    aria-selected={activeTab === CHECKIN_TAB.newDocument}
                    onClick={() => setActiveTab(CHECKIN_TAB.newDocument)}
                    style={{
                      ...segmentBase,
                      backgroundColor:
                        activeTab === CHECKIN_TAB.newDocument
                          ? activeGreen
                          : "#ffffff",
                      color:
                        activeTab === CHECKIN_TAB.newDocument
                          ? "#ffffff"
                          : inactiveTabLabelColor,
                      fontWeight: activeTab === CHECKIN_TAB.newDocument ? 700 : 400,
                      borderColor:
                        activeTab === CHECKIN_TAB.newDocument
                          ? activeGreen
                          : inactiveBorder,
                    }}
                  >
                    New Document
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="checkin-tab-new-revision"
                    aria-selected={activeTab === CHECKIN_TAB.newRevision}
                    aria-disabled={disableNewRevisionTab}
                    disabled={disableNewRevisionTab}
                    onClick={() => {
                      if (!disableNewRevisionTab) {
                        setActiveTab(CHECKIN_TAB.newRevision);
                      }
                    }}
                    style={{
                      ...segmentBase,
                      backgroundColor:
                        activeTab === CHECKIN_TAB.newRevision
                          ? activeGreen
                          : "#ffffff",
                      color:
                        activeTab === CHECKIN_TAB.newRevision
                          ? "#ffffff"
                          : inactiveTabLabelColor,
                      fontWeight: activeTab === CHECKIN_TAB.newRevision ? 700 : 400,
                      borderColor:
                        activeTab === CHECKIN_TAB.newRevision
                          ? activeGreen
                          : inactiveBorder,
                      cursor: disableNewRevisionTab ? "not-allowed" : "pointer",
                      opacity: disableNewRevisionTab ? 0.45 : 1,
                    }}
                  >
                    New Revision
                  </button>
                </Box>
              </Box>
            </Stack.Item>

            <Stack
              grow
              styles={{
                root: {
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                },
              }}
            >
              <Stack.Item
                grow
                styles={{
                  root: {
                    flex: "1 1 auto",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    background: theme.colors.backgroundColor,
                    padding: 0,
                    margin: "0 300px",
                  },
                }}
              >
                {activeTab === CHECKIN_TAB.newDocument && (
                  <div
                    style={{
                      marginRight: 0,
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CheckInNew />
                  </div>
                )}
                {activeTab === CHECKIN_TAB.newRevision && (
                  <div
                    style={{
                      marginRight: 0,
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CheckinNewRevision />
                  </div>
                )}
              </Stack.Item>
            </Stack>
          </Stack>
        </div>
      </Stack.Item>
    </Stack>
  );
}

export default CheckInPage;
